import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { BadgeDollarSign, CheckCircle, AlertTriangle, MapPin, Info, Calculator, Plus, Trash2, Search, Home, ArrowRight, ExternalLink } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    constructionClasses,
    conditionFactors,
    calculateAppraisalInternal
} from '@/utils/appraisalLogic';

const AppraisalEstimator = () => {
    // 1. Inputs State
    const [formData, setFormData] = useState({
        address: '', // Direccion completa o GPS
        landArea: 200, // m2
        landUnitPrice: 150, // USD/m2
        constructionArea: 150, // m2
        age: 5, // Años
        constructionClass: 'MEDIA', // BAJA, MEDIA, ALTA
        condition: 'Bueno' // Nuevo, Bueno, Medio, etc.
    });

    const [results, setResults] = useState(null);

    const calculateAppraisal = () => {
        const results = calculateAppraisalInternal(formData);
        setResults(results);
    };

    // Real-time calculation effect
    useEffect(() => {
        calculateAppraisal();
    }, [formData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleGetLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const { latitude, longitude } = position.coords;
                setFormData(prev => ({ ...prev, address: `GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}` }));
            }, (error) => {
                console.error("Error getting location", error);
                alert("No se pudo obtener la ubicación. Asegúrate de dar permisos.");
            });
        } else {
            alert("Geolocalización no soportada por este navegador.");
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* INPUT SECTION (Left/Top) */}
            <div className="lg:col-span-5 space-y-6">
                <Card className="border-t-4 border-t-secondary shadow-md">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-xl text-primary flex items-center gap-2">
                            <BadgeDollarSign className="w-5 h-5" /> Datos de la Propiedad
                        </CardTitle>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setFormData({
                                address: '',
                                landArea: 0,
                                landUnitPrice: 0,
                                constructionArea: 0,
                                age: 0,
                                constructionClass: 'MEDIA',
                                condition: 'Bueno'
                            })}
                            className="text-xs text-gray-500 hover:text-red-500 h-8"
                        >
                            <Trash2 size={14} className="mr-1" /> Limpiar
                        </Button>
                    </CardHeader>
                    <CardContent className="space-y-5">
                        {/* Location */}
                        <div className="space-y-2">
                            <Label className="flex items-center justify-between">
                                <span className="flex items-center gap-1"><MapPin size={14} /> Dirección / Ubicación GPS</span>
                                <Button variant="outline" size="sm" className="h-auto py-1 px-2 text-xs gap-1" onClick={handleGetLocation}>
                                    <MapPin size={10} /> Usar mi ubicación GPS
                                </Button>
                            </Label>
                            <Input
                                placeholder="Ej: Colonia Escalón, Calle Principal #123... o Coordenadas"
                                value={formData.address}
                                onChange={(e) => handleInputChange('address', e.target.value)}
                            />
                        </div>

                        {/* Land Data */}
                        <div className="p-4 bg-gray-50 rounded-lg space-y-4 border border-gray-100">
                            <div className="flex justify-between items-center mb-2">
                                <h4 className="font-semibold text-secondary text-sm">Datos del Terreno</h4>
                                <LandValueAssistant
                                    onApply={(value) => handleInputChange('landUnitPrice', value)}
                                    constructionClasses={constructionClasses}
                                    conditionFactors={conditionFactors}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Área Terreno (m²)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.landArea}
                                        onChange={(e) => handleInputChange('landArea', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Precio Mercado ($/m²)</Label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2.5 text-gray-500 text-sm">$</span>
                                        <Input
                                            type="number"
                                            min="0"
                                            className="pl-7"
                                            value={formData.landUnitPrice}
                                            onChange={(e) => handleInputChange('landUnitPrice', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Construction Data */}
                        <div className="p-4 bg-gray-50 rounded-lg space-y-4 border border-gray-100">
                            <h4 className="font-semibold text-secondary text-sm">Datos de Construcción</h4>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Área Const. (m²)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.constructionArea}
                                        onChange={(e) => handleInputChange('constructionArea', e.target.value)}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Antigüedad (Años)</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.age}
                                        onChange={(e) => handleInputChange('age', e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label>Clase de Construcción</Label>
                                <Select value={formData.constructionClass} onValueChange={(v) => handleInputChange('constructionClass', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(constructionClasses).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>
                                                {key} - {val.desc.substring(0, 30)}...
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 italic p-2 bg-white rounded border border-gray-100">
                                    {constructionClasses[formData.constructionClass].desc}
                                </p>
                            </div>

                            <div className="space-y-2">
                                <Label>Estado de Conservación</Label>
                                <Select value={formData.condition} onValueChange={(v) => handleInputChange('condition', v)}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(conditionFactors).map(([key, val]) => (
                                            <SelectItem key={key} value={key}>
                                                {key}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-gray-500 italic p-2 bg-white rounded border border-gray-100">
                                    {conditionFactors[formData.condition].desc}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* RESULTS SECTION (Right/Bottom) */}
            <div className="lg:col-span-7 space-y-6">
                {results && (
                    <>
                        <Card className="bg-gradient-to-br from-[#1A3D5C] to-[#162e46] text-white border-none shadow-xl overflow-hidden relative">
                            {/* Decorative element */}
                            <div className="absolute top-0 right-0 p-4 opacity-10">
                                <Calculator size={120} />
                            </div>

                            <CardContent className="p-6 md:p-8 text-center relative z-10">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold mb-6 border border-white/20">
                                    <CheckCircle size={12} className="text-green-400" /> ESTIMACIÓN REFERENCIAL (NO OFICIAL)
                                </div>

                                <h3 className="text-sm font-medium opacity-80 uppercase tracking-widest mb-1">Valor de Mercado Estimado</h3>
                                <div className="text-4xl md:text-5xl font-bold mb-2">
                                    ${results.mid.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                                </div>

                                <div className="flex justify-center items-center gap-4 mb-8">
                                    <div className="text-left bg-white/5 border border-white/10 rounded-lg p-2 flex-1 max-w-[140px]">
                                        <p className="text-[10px] opacity-60 uppercase mb-1">Rango Bajo</p>
                                        <p className="text-lg font-semibold text-red-200">${results.low.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <div className="text-left bg-white/5 border border-white/10 rounded-lg p-2 flex-1 max-w-[140px]">
                                        <p className="text-[10px] opacity-60 uppercase mb-1">Rango Alto</p>
                                        <p className="text-lg font-semibold text-green-200">${results.high.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-sm opacity-90 border-t border-white/20 pt-6">
                                    <div className="text-center">
                                        <p className="text-[10px] uppercase tracking-wider mb-1 opacity-60">Confianza del Cálculo</p>
                                        <div className="flex items-center justify-center gap-2">
                                            <span className={`h-2 w-2 rounded-full ${results.confidence === 'Alta' ? 'bg-green-400' : results.confidence === 'Media' ? 'bg-yellow-400' : 'bg-red-400'}`}></span>
                                            <p className="font-bold text-base">{results.confidence} ({results.confidencePoints}%)</p>
                                        </div>
                                    </div>
                                    <div className="text-center border-l border-white/20">
                                        <p className="text-[10px] uppercase tracking-wider mb-1 opacity-60">Valor Terreno / m²</p>
                                        <p className="font-bold text-base">${parseFloat(formData.landUnitPrice).toLocaleString()}</p>
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-center">
                                    <Dialog>
                                        <DialogTrigger asChild>
                                            <Button variant="outline" className="bg-white/10 border-white/20 hover:bg-white/20 text-xs h-9 flex items-center gap-2">
                                                <ExternalLink size={14} /> Exportar Reporte Premium
                                            </Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Funcionalidad Premium</DialogTitle>
                                                <DialogDescription className="space-y-4 pt-4">
                                                    <div className="p-4 bg-primary/5 rounded-lg border border-primary/10">
                                                        <p className="text-primary font-semibold mb-2">Para generar y exportar reportes detallados en PDF necesitas una suscripción activa.</p>
                                                        <ul className="text-sm space-y-2 text-gray-600">
                                                            <li className="flex gap-2">✅ Reporte detallado para clientes</li>
                                                            <li className="flex gap-2">✅ Comparativa de mercado incluida</li>
                                                            <li className="flex gap-2">✅ Sin marcas de agua publicitarias</li>
                                                        </ul>
                                                    </div>
                                                    <Button className="w-full bg-secondary hover:bg-secondary/90">Ver Planes Disponibles</Button>
                                                </DialogDescription>
                                            </DialogHeader>
                                        </DialogContent>
                                    </Dialog>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Summary breakdown */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <Card className="p-4 bg-white border shadow-sm">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase">Valor Terreno (Vt)</p>
                                        <p className="text-xl font-bold text-primary">${results.vt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <MapPin size={24} className="text-gray-200" />
                                </div>
                            </Card>
                            <Card className="p-4 bg-white border shadow-sm">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase">Valor Edificación (Vc)</p>
                                        <p className="text-xl font-bold text-primary">${results.vc.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                                    </div>
                                    <Home size={24} className="text-gray-200" />
                                </div>
                            </Card>
                        </div>

                        {/* Didactic Explanation */}
                        <Card className="border-l-4 border-l-secondary bg-white shadow-sm">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                                    <Info className="w-5 h-5 text-secondary" /> Justificación Metodológica
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6 text-sm text-gray-600">

                                {/* Vt Explanation */}
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                    <h4 className="font-bold text-gray-800 mb-2">1. Valor del Terreno (Vt)</h4>
                                    <p className="mb-2">El valor del terreno se calcula multiplicando su área por el precio unitario de mercado de la zona.</p>
                                    <div className="font-mono bg-white p-2 rounded border border-gray-200 text-xs overflow-x-auto">
                                        Vt = {formData.landArea} m² × ${formData.landUnitPrice}/m² = <strong>${results.vt.toLocaleString()}</strong>
                                    </div>
                                </div>

                                {/* Vc Explanation */}
                                <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                    <h4 className="font-bold text-gray-800 mb-2">2. Valor de la Construcción (Vc) - Método de Costo de Reposición</h4>
                                    <p className="mb-2">
                                        Se determina calculando cuánto costaría construir el inmueble nuevo hoy (VRN) y aplicando factores de corrección por antigüedad y estado físico.
                                    </p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                                        <div className="space-y-1">
                                            <p className="text-xs font-semibold uppercase text-gray-400">Parámetros</p>
                                            <ul className="list-disc pl-5 space-y-1 text-xs">
                                                <li><strong>Clase:</strong> {formData.constructionClass} (VRN: ${results.vrn}/m²)</li>
                                                <li><strong>Estado:</strong> {formData.condition} (Fe: {results.fe})</li>
                                                <li><strong>Antigüedad:</strong> {formData.age} años</li>
                                            </ul>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs font-semibold uppercase text-gray-400">Factores Aplicados</p>
                                            <ul className="list-disc pl-5 space-y-1 text-xs">
                                                <li><strong>Deprec. Edad:</strong> {(results.ageDepreciation * 100).toFixed(1)}%</li>
                                                <li><strong>Factor Conservación:</strong> {(results.fe * 100).toFixed(1)}%</li>
                                                <li><strong>Factor Combinado:</strong> {(results.combinedFactor * 100).toFixed(1)}%</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="font-mono bg-white p-2 rounded border border-gray-200 text-[10px] mb-2 overflow-x-auto">
                                        Vc = Area ({formData.constructionArea}m²) × VRN (${results.vrn}) × Factor Combinado ({results.combinedFactor.toFixed(4)})
                                    </div>
                                    <div className="font-mono bg-white p-2 rounded border border-gray-200 text-sm overflow-x-auto text-center">
                                        Vc = <strong>${results.vc.toLocaleString(undefined, { maximumFractionDigits: 0 })}</strong>
                                    </div>

                                    <div className="mt-4 p-3 bg-blue-50 rounded border border-blue-100 italic text-xs">
                                        <strong>Resumen de Análisis:</strong>
                                        <ul className="mt-2 space-y-1">
                                            {results.explanations.map((exp, idx) => (
                                                <li key={idx} className="flex gap-2"><span>•</span> {exp}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </>
                )}

                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 flex gap-3">
                    <Info className="w-6 h-6 text-blue-600 flex-shrink-0" />
                    <div className="text-xs text-blue-800">
                        <strong>⚖️ Avalúo orientativo.</strong><br />
                        Esta herramienta ofrece una estimación de referencia y no sustituye un avalúo certificado según la normativa de El Salvador.
                    </div>
                </div>
            </div>
        </div>
    );
};

const LandValueAssistant = ({ onApply, constructionClasses, conditionFactors }) => {
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState("comparison");

    // 1. Comparison State
    const [samples, setSamples] = useState([]);
    const [newSample, setNewSample] = useState({ price: '', area: '', url: '' });

    const addSample = () => {
        if (newSample.price && newSample.area) {
            const price = parseFloat(newSample.price);
            const area = parseFloat(newSample.area);
            const vut = price / area;
            setSamples([...samples, { ...newSample, price, area, vut }]);
            setNewSample({ price: '', area: '', url: '' });
        }
    };

    const removeSample = (index) => {
        setSamples(samples.filter((_, i) => i !== index));
    };

    const avgVut = samples.length > 0 ? samples.reduce((acc, curr) => acc + curr.vut, 0) / samples.length : 0;

    // 2. Location State
    const [locAddress, setLocAddress] = useState('');
    const [locAdjustment, setLocAdjustment] = useState(0); // -3 to 5
    const [baseLocValue, setBaseLocValue] = useState(150); // Default base

    const handleLocSearch = () => {
        // Mock search logic
        const lowerAddr = locAddress.toLowerCase();
        if (lowerAddr.includes('escalon') || lowerAddr.includes('san benito')) setBaseLocValue(400);
        else if (lowerAddr.includes('santa elena')) setBaseLocValue(350);
        else if (lowerAddr.includes('santa ana')) setBaseLocValue(150);
        else if (lowerAddr.includes('soyapango')) setBaseLocValue(60);
        else setBaseLocValue(100);
    };

    const adjustedLocValue = baseLocValue * (1 + (locAdjustment / 100));

    // 3. Inverse State
    const [invData, setInvData] = useState({
        totalPrice: 150000,
        landArea: 200,
        constArea: 100,
        age: 10,
        constructionClass: 'MEDIA',
        condition: 'Bueno'
    });
    const [invResult, setInvResult] = useState(null);

    const calculateInverse = () => {
        const vrn = constructionClasses[invData.constructionClass].vrn;
        const fe = conditionFactors[invData.condition].fe;
        const vc = invData.constArea * vrn * fe;
        const vt = invData.totalPrice - vc;
        const vut = vt / invData.landArea;
        setInvResult({ vc, vt, vut });
    };

    const handleApply = (val) => {
        onApply(Math.round(val * 100) / 100);
        setOpen(false);
    };

    const handleGetLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((position) => {
                const { latitude, longitude } = position.coords;
                setLocAddress(`GPS: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
            }, (error) => {
                console.error("Error getting location", error);
                alert("No se pudo obtener la ubicación.");
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 border-primary text-primary hover:bg-primary hover:text-white">
                    <Calculator size={12} /> Asistente de Precio
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Asistente para Valor de Terreno ($/m²)</DialogTitle>
                    <DialogDescription>
                        Utiliza una de estas 3 herramientas para estimar el precio de mercado del terreno.
                    </DialogDescription>
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="comparison">Comparación</TabsTrigger>
                        <TabsTrigger value="location">Ubicación</TabsTrigger>
                        <TabsTrigger value="inverse">Método Inverso</TabsTrigger>
                    </TabsList>

                    {/* 1. COMPARISON TOOL */}
                    <TabsContent value="comparison" className="space-y-4 py-4">
                        <div className="bg-blue-50 p-3 rounded-md text-sm text-blue-800 mb-4">
                            Agrega enlaces de propiedades similares (terrenos o casas para demoler) para obtener un promedio.
                        </div>
                        <div className="grid grid-cols-12 gap-2 items-end">
                            <div className="col-span-12 md:col-span-5 space-y-1">
                                <Label className="text-xs">Enlace (Opcional)</Label>
                                <Input
                                    placeholder="https://..."
                                    value={newSample.url}
                                    onChange={e => setNewSample({ ...newSample, url: e.target.value })}
                                    className="h-8 text-xs"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-3 space-y-1">
                                <Label className="text-xs">Precio ($)</Label>
                                <div className="relative">
                                    <span className="absolute left-2 top-1.5 text-gray-500 text-xs">$</span>
                                    <Input
                                        type="number"
                                        placeholder="0.00"
                                        value={newSample.price}
                                        onChange={e => setNewSample({ ...newSample, price: e.target.value })}
                                        className="h-8 text-xs pl-5"
                                    />
                                </div>
                            </div>
                            <div className="col-span-4 md:col-span-2 space-y-1">
                                <Label className="text-xs">Área (m²)</Label>
                                <Input
                                    type="number"
                                    placeholder="0"
                                    value={newSample.area}
                                    onChange={e => setNewSample({ ...newSample, area: e.target.value })}
                                    className="h-8 text-xs"
                                />
                            </div>
                            <div className="col-span-4 md:col-span-2">
                                <Button onClick={addSample} size="sm" className="w-full h-8 bg-secondary"><Plus size={14} /></Button>
                            </div>
                        </div>

                        <div className="border rounded-md overflow-hidden">
                            <table className="w-full text-sm text-left">
                                <thead className="bg-gray-100 text-gray-600">
                                    <tr>
                                        <th className="p-2 font-medium">Precio</th>
                                        <th className="p-2 font-medium">Área</th>
                                        <th className="p-2 font-medium">$/m²</th>
                                        <th className="p-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {samples.map((s, i) => (
                                        <tr key={i} className="border-t">
                                            <td className="p-2">${s.price.toLocaleString()}</td>
                                            <td className="p-2">{s.area} m²</td>
                                            <td className="p-2 font-bold">${s.vut.toFixed(2)}</td>
                                            <td className="p-2 text-right">
                                                <button onClick={() => removeSample(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                    {samples.length === 0 && (
                                        <tr><td colSpan="4" className="p-4 text-center text-gray-400 text-xs">No hay muestras agregadas</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {samples.length > 0 && (
                            <div className="flex justify-between items-center bg-gray-100 p-4 rounded-lg">
                                <div>
                                    <p className="text-xs text-gray-500">Promedio Calculado</p>
                                    <p className="text-2xl font-bold text-primary">${avgVut.toFixed(2)} / m²</p>
                                </div>
                                <Button onClick={() => handleApply(avgVut)} className="bg-green-600 hover:bg-green-700 text-white">
                                    Usar este Valor
                                </Button>
                            </div>
                        )}
                    </TabsContent>

                    {/* 2. LOCATION TOOL */}
                    <TabsContent value="location" className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label className="flex items-center justify-between">
                                <span>Buscar Zona / Colonia</span>
                                <Button variant="outline" size="sm" className="h-auto py-1 px-2 text-xs gap-1" onClick={handleGetLocation}>
                                    <MapPin size={10} /> Usar mi ubicación GPS
                                </Button>
                            </Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Ej: Santa Ana Centro, Escalon..."
                                    value={locAddress}
                                    onChange={e => setLocAddress(e.target.value)}
                                />
                                <Button onClick={handleLocSearch} variant="outline"><Search size={16} /></Button>
                            </div>
                            <p className="text-xs text-gray-500">Busca una zona para obtener un valor base referencial.</p>
                        </div>

                        <div className="p-4 border rounded-lg space-y-6">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium">Valor Base Zona:</span>
                                <span className="text-lg font-bold">${baseLocValue.toFixed(2)}</span>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between text-xs text-gray-600 font-medium">
                                    <span className="text-red-500">Pasaje / Interior (-3%)</span>
                                    <span className="text-gray-500">Estándar (0%)</span>
                                    <span className="text-green-600">Esquina / Parque (+5%)</span>
                                </div>
                                <Slider
                                    value={[locAdjustment]}
                                    min={-3}
                                    max={5}
                                    step={1}
                                    onValueChange={(v) => setLocAdjustment(v[0])}
                                    className="py-2"
                                />
                                <div className="text-center">
                                    <div className="text-sm font-bold text-secondary mb-1">
                                        Ajuste de Ubicación: {locAdjustment > 0 ? '+' : ''}{locAdjustment}%
                                    </div>
                                    <p className="text-xs text-gray-500">
                                        {locAdjustment === -3 && "Ubicación interior, pasaje peatonal o acceso limitado."}
                                        {locAdjustment === -2 && "Calle secundaria con poco tráfico o acceso regular."}
                                        {locAdjustment === -1 && "Ubicación estándar con ligeras desventajas."}
                                        {locAdjustment === 0 && "Ubicación estándar en calle vehicular."}
                                        {locAdjustment === 1 && "Buena ubicación, calle amplia."}
                                        {locAdjustment === 2 && "Muy buena ubicación, cerca de accesos principales."}
                                        {locAdjustment === 3 && "Zona de alta plusvalía o tráfico moderado."}
                                        {locAdjustment === 4 && "Ubicación preferencial, vistas o entorno superior."}
                                        {locAdjustment === 5 && "Ubicación premium: Esquina, frente a parque o zona comercial."}
                                    </p>
                                </div>
                            </div>

                            <div className="pt-4 border-t flex justify-between items-center">
                                <div>
                                    <p className="text-xs text-gray-500">Valor Ajustado</p>
                                    <p className="text-2xl font-bold text-primary">${adjustedLocValue.toFixed(2)} / m²</p>
                                </div>
                                <Button onClick={() => handleApply(adjustedLocValue)} className="bg-green-600 hover:bg-green-700 text-white">
                                    Usar este Valor
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* 3. INVERSE TOOL */}
                    <TabsContent value="inverse" className="space-y-4 py-4">
                        <div className="bg-orange-50 p-3 rounded-md text-sm text-orange-800 mb-4">
                            Usa una casa vecina vendida para estimar el valor del terreno restando la construcción.
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label className="text-xs">Precio Venta Total ($)</Label>
                                <div className="relative">
                                    <span className="absolute left-2 top-1.5 text-gray-500 text-xs">$</span>
                                    <Input
                                        type="number"
                                        value={invData.totalPrice}
                                        onChange={e => setInvData({ ...invData, totalPrice: parseFloat(e.target.value) })}
                                        className="pl-5"
                                    />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Área Terreno (m²)</Label>
                                <Input
                                    type="number"
                                    value={invData.landArea}
                                    onChange={e => setInvData({ ...invData, landArea: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Área Const. (m²)</Label>
                                <Input
                                    type="number"
                                    value={invData.constArea}
                                    onChange={e => setInvData({ ...invData, constArea: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Antigüedad (Años)</Label>
                                <Input
                                    type="number"
                                    value={invData.age}
                                    onChange={e => setInvData({ ...invData, age: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Clase</Label>
                                <Select value={invData.constructionClass} onValueChange={(v) => setInvData({ ...invData, constructionClass: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(constructionClasses).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Estado</Label>
                                <Select value={invData.condition} onValueChange={(v) => setInvData({ ...invData, condition: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.keys(conditionFactors).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <Button onClick={calculateInverse} variant="secondary" className="w-full mt-2">Calcular Valor Implícito</Button>

                        {invResult && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span>Valor Construcción Est.:</span>
                                    <span className="font-medium">${invResult.vc.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span>Valor Terreno Implícito:</span>
                                    <span className="font-medium">${invResult.vt.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                </div>
                                <div className="pt-2 border-t flex justify-between items-center mt-2">
                                    <div>
                                        <p className="text-xs text-gray-500">VUT Resultante</p>
                                        <p className="text-xl font-bold text-primary">${invResult.vut.toFixed(2)} / m²</p>
                                    </div>
                                    <Button onClick={() => handleApply(invResult.vut)} className="bg-green-600 hover:bg-green-700 text-white size-sm">
                                        Usar
                                    </Button>
                                </div>
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
    );
};

export default AppraisalEstimator;
